
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDynamicStyle } from '../hooks/useDynamicStyle';
import {
    ZoomInIcon, ZoomOutIcon, MaximizeIcon, RotateCcwIcon, SaveIcon,
    ShoppingBagIcon, HeartIcon, XIcon, ChevronLeftIcon, ChevronRightIcon,
    LockIcon, Rotate3dIcon, CubeIcon, SlidersHorizontalIcon, WandSparklesIcon, BadgeCheckIcon,
    VideoIcon, PlayIcon, MessageSquareIcon, PaletteIcon
} from './icons';
import { useTranslation } from '../i18n/useTranslation';
import Spinner from './Spinner';
import ThreeSixtyViewer from './ThreeSixtyViewer';
import CreativeToolsPanel from './CreativeToolsPanel';
import StyleReportModal from './StyleReportModal';
import PremiumFeatureModal from './PremiumFeatureModal';
import {
    analyzeFitConfidence, analyzeFitMap, generateShoppingLookVideo,
    getVideosOperationStatus, generateComprehensiveReport, extractVideoUrlFromOperation, downloadVideo
} from '../services/geminiService';
import { apiKeys } from '../config';
import { hasFeatureAccess } from '@shared/services/userService';
import Footer from './Footer';
import { CDN_URLS } from '../config';
import {
    PoseCategory, Pose, UserProfile, OutfitLayer, SavedOutfit, SceneConfiguration,
    StyleReport
} from '../types';
import { POSE_CATEGORIES } from '../constants';
import { useSEO } from '../hooks/useSEO';
import { getMultiCdnUrl } from '../utils/imageUtils';

// hasAccess local helper removed in favor of userService.hasFeatureAccess

// Hook for scroll controls
const useScrollControls = () => {
    const ref = useRef<HTMLDivElement>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const checkScroll = useCallback(() => {
        if (ref.current) {
            const { scrollLeft, scrollWidth, clientWidth } = ref.current;
            setCanScrollLeft(scrollLeft > 0);
            setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
        }
    }, []);

    useEffect(() => {
        const el = ref.current;
        if (el) {
            el.addEventListener('scroll', checkScroll);
            window.addEventListener('resize', checkScroll);
            const timer = setTimeout(checkScroll, 100);
            return () => {
                el.removeEventListener('scroll', checkScroll);
                window.removeEventListener('resize', checkScroll);
                clearTimeout(timer);
            };
        }
    }, [checkScroll]);

    const scroll = (direction: 'left' | 'right') => {
        if (ref.current) {
            const amount = direction === 'left' ? -200 : 200;
            ref.current.scrollBy({ left: amount, behavior: 'smooth' });
        }
    };

    return { ref, canScrollLeft, canScrollRight, scroll, checkScroll };
};

interface CanvasProps {
    displayImageUrl: string | null;
    baseModelImageUrl: string | null;
    isLoading: boolean;
    loadingMessage: string;
    loadingAction: string | null;
    generationError: string | null;
    onClearError: () => void;
    onSelectPose: (pose: string) => void;
    poseCategories: PoseCategory[];
    currentPoseInstruction: string;
    availablePoseKeys: string[];
    onShopTheLook: (context?: string) => void;
    isShopTheLookEnabled: boolean;
    isFavorited: boolean;
    onToggleFavorite: () => void;
    isFavoriteEnabled: boolean;
    userProfile: UserProfile;
    isComparisonMode: boolean;
    onToggleComparisonMode: () => void;
    onSaveOutfit: () => void;
    onStartOver: () => void;
    comparisonOutfits: SavedOutfit[] | null;
    currentOutfit: OutfitLayer;
    onUpdateSceneConfig: (config: SceneConfiguration) => void;
    onGenerate3DView: () => Promise<boolean>;
    view360Images: string[];
    onToggleFavoritePose: (pose: string) => void;
    onNavigate: (page: any, params?: any) => void;
    onApplyCreativeTool?: (type: 'ageing' | 'wrinkles' | 'golden_hour' | 'remove_glasses' | 'remove_hat' | 'remove_watch' | 'remove_bag') => Promise<void>;
    onSaveReport?: (report: StyleReport, imageUrl: string) => void;
    styleReport: StyleReport | null;
    onCloseReport: () => void;
    onGenerateReport: () => void;
}

const PoseBar: React.FC<CanvasProps> = (props) => {
    const { poseCategories, currentPoseInstruction, onSelectPose, userProfile, onToggleFavoritePose } = props;
    const { t } = useTranslation();

    // Create a dynamic list of categories including Favorites
    const categoriesWithFavorites = useMemo(() => {
        const allPoses = POSE_CATEGORIES.flatMap(category => category.poses);
        const favoritePosesList = allPoses.filter(p => userProfile.favoritePoses?.includes(p.instruction));

        const favoritesCategory: PoseCategory = {
            nameKey: 'pose_category_favorites',
            poses: favoritePosesList,
        };

        // Only add Favorites category if there are favorites
        return favoritePosesList.length > 0
            ? [favoritesCategory, ...poseCategories]
            : poseCategories;
    }, [poseCategories, userProfile.favoritePoses]);

    const [activeCategory, setActiveCategory] = useState<string>(categoriesWithFavorites[0]?.nameKey || '');

    // Reset active category if favorites become empty and it was selected
    useEffect(() => {
        if (activeCategory === 'pose_category_favorites' && (!userProfile.favoritePoses || userProfile.favoritePoses.length === 0)) {
            setActiveCategory(poseCategories[0].nameKey);
        }
    }, [userProfile.favoritePoses, activeCategory, poseCategories]);

    const [hoveredPose, setHoveredPose] = useState<Pose | null>(null);

    const categoryScroll = useScrollControls();
    const poseScroll = useScrollControls();

    const posesForActiveCategory = useMemo(() => {
        return categoriesWithFavorites.find(c => c.nameKey === activeCategory)?.poses || [];
    }, [activeCategory, categoriesWithFavorites]);

    useEffect(() => {
        poseScroll.checkScroll();
    }, [posesForActiveCategory, poseScroll]);

    return (
        <div id="onboarding-pose-bar" className="sticky bottom-0 left-0 right-0 bg-off-white/90 dark:bg-deep-charcoal/90 backdrop-blur-lg p-3 z-20 border-t border-gray-200 dark:border-gray-700">
            {/* Pose Selector (Moved Top) */}
            <div className="relative mb-2 sm:mb-3">
                {poseScroll.canScrollLeft && <button onClick={() => poseScroll.scroll('left')} className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/50 dark:bg-gray-800/50 p-1 rounded-full shadow-md" title={t('common_scrollLeft')}><ChevronLeftIcon className="w-5 h-5" /></button>}
                <div ref={poseScroll.ref} className="flex gap-2 sm:gap-3 overflow-x-auto scrollbar-hide h-20 sm:h-24 items-center">
                    {posesForActiveCategory.map(pose => {
                        const cat = categoriesWithFavorites.find(c => c.nameKey === activeCategory);
                        const isLocked = !hasFeatureAccess(userProfile, undefined, cat?.requiredMembership);
                        const isSelected = currentPoseInstruction === pose.instruction;
                        const fullPreviewUrl = getMultiCdnUrl(pose.previewUrl, 'WARDROBE');
                        const isFavorited = userProfile.favoritePoses?.includes(pose.instruction);

                        return (
                            <button
                                key={pose.instruction}
                                onClick={() => onSelectPose(pose.instruction)}
                                disabled={isLocked}
                                onMouseEnter={() => setHoveredPose(pose)}
                                onMouseLeave={() => setHoveredPose(null)}
                                onFocus={() => setHoveredPose(pose)}
                                onBlur={() => setHoveredPose(null)}
                                className={`relative w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden border-2 transition-all duration-200 flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 group ${isSelected ? 'border-indigo-500 scale-105' : 'border-transparent hover:border-gray-400'} disabled:opacity-50 disabled:cursor-not-allowed`}
                            >
                                <img src={fullPreviewUrl} alt={t(pose.labelKey)} className="w-full h-full object-cover" />
                                {isLocked && <div className="absolute inset-0 bg-black/60 flex items-center justify-center"><LockIcon className="w-8 h-8 text-white/80" /></div>}

                                {onToggleFavoritePose && !isLocked && (
                                    <div
                                        role="button"
                                        tabIndex={0}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onToggleFavoritePose(pose.instruction);
                                        }}
                                        className="absolute top-1 right-1 p-1 rounded-full bg-black/20 hover:bg-black/40 transition-colors"
                                        title={isFavorited ? t('canvas_favorite_remove') : t('canvas_favorite_add')}
                                    >
                                        <HeartIcon filled={isFavorited} className={`w-3 h-3 ${isFavorited ? 'text-red-500' : 'text-white'}`} />
                                    </div>
                                )}
                            </button>
                        );
                    })}
                </div>
                {poseScroll.canScrollRight && <button onClick={() => poseScroll.scroll('right')} className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/50 dark:bg-gray-800/50 p-1 rounded-full shadow-md" title={t('common_scrollRight')}><ChevronRightIcon className="w-5 h-5" /></button>}
            </div>

            {/* Category Selector (Moved Bottom) */}
            <div className="relative">
                {categoryScroll.canScrollLeft && <button onClick={() => categoryScroll.scroll('left')} className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/50 dark:bg-gray-800/50 p-1 rounded-full shadow-md" title={t('common_scrollLeft')}><ChevronLeftIcon className="w-5 h-5" /></button>}
                <div ref={categoryScroll.ref} className="flex gap-3 overflow-x-auto scrollbar-hide">
                    {categoriesWithFavorites.map(cat => (
                        <button key={cat.nameKey} onClick={() => setActiveCategory(cat.nameKey)} className={`px-4 py-1.5 text-sm font-semibold rounded-full whitespace-nowrap transition-colors ${activeCategory === cat.nameKey ? 'bg-indigo-600 text-white' : 'bg-gray-200/80 text-gray-700 hover:bg-gray-300 dark:bg-gray-700/80 dark:text-gray-200 dark:hover:bg-gray-600'}`}>
                            {t(cat.nameKey)}
                        </button>
                    ))}
                </div>
                {categoryScroll.canScrollRight && <button onClick={() => categoryScroll.scroll('right')} className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/50 dark:bg-gray-800/50 p-1 rounded-full shadow-md" title={t('common_scrollRight')}><ChevronRightIcon className="w-5 h-5" /></button>}
            </div>

            <AnimatePresence>
                {hoveredPose && (
                    <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                        className="absolute bottom-full left-1/2 -translate-x-1/2 mb-4 p-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border dark:border-gray-700 pointer-events-none"
                    >
                        <img src={getMultiCdnUrl(hoveredPose.previewUrl, 'WARDROBE')} alt="Preview" className="w-24 h-24 object-cover rounded-md" />
                        <p className="text-xs font-semibold text-center mt-1 text-gray-900 dark:text-gray-100">{t(hoveredPose.labelKey)}</p>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

const SceneControlPanel: React.FC<{
    config: SceneConfiguration | undefined;
    onUpdate: (config: SceneConfiguration) => void;
    isOpen: boolean;
    onClose: () => void;
}> = ({ config, onUpdate, isOpen, onClose }) => {
    const { t } = useTranslation();
    const defaultConfig: SceneConfiguration = {
        cameraView: 'full-body',
        backgroundPrompt: 'neutral light gray studio backdrop',
        modelPosition: { x: 0, y: 0 },
        modelScale: 1
    };

    const currentConfig = config || defaultConfig;
    const [localPrompt, setLocalPrompt] = useState(currentConfig.backgroundPrompt);

    const updateField = (field: keyof SceneConfiguration, value: any) => {
        onUpdate({ ...currentConfig, [field]: value });
    };

    const bgSuggestions = [
        { labelKey: 'sceneControl_bg_studio', prompt: 'neutral light gray studio backdrop' },
        { labelKey: 'sceneControl_bg_street', prompt: 'busy new york city street, blurred background, daytime' },
        { labelKey: 'sceneControl_bg_nature', prompt: 'peaceful forest clearing with sunlight filtering through trees' },
        { labelKey: 'sceneControl_bg_cafe', prompt: 'cozy parisian cafe terrace, sunny afternoon' },
        { labelKey: 'sceneControl_bg_beach', prompt: 'golden hour beach sunset, calm ocean' },
    ];

    if (!isOpen) return null;

    return (
        <div className="absolute top-16 left-4 z-30 bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 p-4 w-72 max-h-[80vh] overflow-y-auto custom-scrollbar">
            <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-gray-800 dark:text-white">{t('sceneControl_title')}</h3>
                <button onClick={onClose} className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full" title={t('common_close')}>
                    <XIcon className="w-4 h-4" />
                </button>
            </div>

            <div className="space-y-4">
                {/* Background */}
                <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase mb-2 block">{t('sceneControl_background_label')}</label>
                    <div className="grid grid-cols-2 gap-2 mb-2">
                        {bgSuggestions.map(bg => (
                            <button
                                key={bg.labelKey}
                                onClick={() => { setLocalPrompt(bg.prompt); updateField('backgroundPrompt', bg.prompt); }}
                                className={`text-xs p-2 rounded border transition-colors ${currentConfig.backgroundPrompt === bg.prompt ? 'bg-indigo-100 border-indigo-500 text-indigo-700' : 'bg-white border-gray-200 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200'}`}
                            >
                                {t(bg.labelKey)}
                            </button>
                        ))}
                    </div>
                    <textarea
                        value={localPrompt}
                        onChange={(e) => setLocalPrompt(e.target.value)}
                        onBlur={() => updateField('backgroundPrompt', localPrompt)}
                        className="w-full text-xs p-2 border rounded dark:bg-gray-700 dark:border-gray-600"
                        rows={2}
                        placeholder={t('sceneControl_custom_bg_placeholder')}
                    />
                </div>

                {/* Model Scale */}
                <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase mb-2 block">{t('sceneControl_modelScale_label')} ({currentConfig.modelScale.toFixed(2)})</label>
                    <input
                        type="range"
                        min="0.5"
                        max="1.5"
                        step="0.05"
                        value={currentConfig.modelScale}
                        onChange={(e) => updateField('modelScale', parseFloat(e.target.value))}
                        className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                        title={t('sceneControl_modelScale_label')}
                    />
                </div>
            </div>
        </div>
    );
};

// Helper component for Fit Heatmap to use useDynamicStyle
const FitHeatmapArea: React.FC<{ area: any; t: any }> = ({ area, t }) => {
    const styleRef = useDynamicStyle<HTMLDivElement>({ '--area-x': `${area.x}%`, '--area-y': `${area.y}%` });

    return (
        <motion.div
            ref={styleRef}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="absolute -translate-x-1/2 -translate-y-1/2 group pointer-events-auto left-[var(--area-x)] top-[var(--area-y)]"
        >
            <div className={`w-12 h-12 rounded-full opacity-60 ${area.status === 'perfect' ? 'bg-green-500' : area.status === 'good' ? 'bg-yellow-500' : area.status === 'loose' ? 'bg-yellow-500' : 'bg-red-500'} blur-xl`} />
            <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-black/80 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                {area.name}: {t(`fit_status_${area.status}`, area.status)}
            </div>
        </motion.div>
    );
};

const ConfidenceScoreBar: React.FC<{ score: number }> = ({ score }) => {
    const styleRef = useDynamicStyle<HTMLDivElement>({ '--score-width': `${score}%` });
    return (
        <div
            ref={styleRef}
            className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 w-[var(--score-width)]"
        />
    );
};

const Canvas: React.FC<CanvasProps> = (props) => {
    const {
        displayImageUrl, baseModelImageUrl, isLoading, loadingMessage, loadingAction, generationError,
        onClearError, isShopTheLookEnabled, onShopTheLook, isFavorited, onToggleFavorite, isFavoriteEnabled,
        comparisonOutfits, onGenerate3DView, view360Images, currentOutfit, onUpdateSceneConfig, userProfile, onNavigate,
        styleReport, onCloseReport, onGenerateReport
    } = props;
    const { t, lang } = useTranslation();
    const [is3DMode, setIs3DMode] = useState(false);
    const [isSceneControlOpen, setIsSceneControlOpen] = useState(false);
    const [isCreativeToolsOpen, setIsCreativeToolsOpen] = useState(false);
    const [isImageLoading, setIsImageLoading] = useState(false);
    const [confidenceScore, setConfidenceScore] = useState<{ score: number, reason: string } | null>(null);
    const [fitMap, setFitMap] = useState<{ name: string, status: 'tight' | 'perfect' | 'loose', x: number, y: number }[] | null>(null);
    const [isAnalyzingConfidence, setIsAnalyzingConfidence] = useState(false);
    const [isGeneratingReport, setIsGeneratingReport] = useState(false);
    const [videoUrl, setVideoUrl] = useState<string | null>(null);
    const [isVideoLoading, setIsVideoLoading] = useState(false);
    const [lockedFeature, setLockedFeature] = useState<{ id: string, level: string } | null>(null);

    const isFeatureLocked = (requiredLevel: 'basic' | 'premium' | 'advanced' | 'nextgen', featureId?: string): boolean => {
        return !hasFeatureAccess(userProfile, featureId, requiredLevel);
    };

    const handleFeatureClick = (requiredLevel: 'basic' | 'premium' | 'advanced' | 'nextgen', featureId: string, action: () => void) => {
        if (isFeatureLocked(requiredLevel, featureId)) {
            setLockedFeature({ id: featureId, level: requiredLevel });
        } else {
            action();
        }
    };

    // Reset image loading when URL changes
    useEffect(() => {
        if (displayImageUrl) {
            setIsImageLoading(true);
        }
    }, [displayImageUrl]);

    useSEO(
        t('canvas_seo_title'),
        t('canvas_seo_desc'),
        t('canvas_seo_keywords').split(',').map(kw => kw.trim())
    );

    // Safety timeout for loading state
    useEffect(() => {
        let timeout: NodeJS.Timeout;
        if (isImageLoading) {
            timeout = setTimeout(() => {
                setIsImageLoading(false);
            }, 10000); // 10s max wait
        }
        return () => clearTimeout(timeout);
    }, [isImageLoading]);

    // Auto-generate 3D on toggle if not ready
    const handleToggle3D = async () => {
        if (!is3DMode) {
            const success = await onGenerate3DView();
            if (success) setIs3DMode(true);
        } else {
            setIs3DMode(false);
        }
    };

    return (
        <div id="onboarding-canvas-container" className="relative w-full h-full flex flex-col overflow-hidden bg-gray-100 dark:bg-gray-900">
            {/* Top Toolbar */}
            <div className="absolute top-4 left-4 right-4 z-30 flex items-center">
                <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-2 mask-linear-right pointer-events-auto">
                    <button
                        onClick={() => setIsSceneControlOpen(!isSceneControlOpen)}
                        className="p-2 sm:p-3 bg-white/90 dark:bg-gray-800/90 backdrop-blur rounded-full shadow-lg hover:scale-105 transition-transform text-gray-700 dark:text-gray-200 flex-shrink-0"
                        title={t('sceneControl_title')}
                    >
                        <SlidersHorizontalIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                    <button
                        onClick={() => handleFeatureClick('premium', 'wardrobe_3d', async () => {
                            if (!displayImageUrl) return;
                            setIsAnalyzingConfidence(true);
                            setFitMap(null);
                            try {
                                const [confidence, map] = await Promise.all([
                                    analyzeFitConfidence(displayImageUrl, props.userProfile),
                                    analyzeFitMap(displayImageUrl, props.userProfile)
                                ]);
                                setConfidenceScore(confidence);
                                setFitMap(map.areas);
                            } catch (e) {
                                console.error(e);
                            } finally {
                                setIsAnalyzingConfidence(false);
                            }
                        })}
                        disabled={!displayImageUrl || isAnalyzingConfidence}
                        className={`p-2 sm:p-3 bg-white/90 dark:bg-gray-800/90 backdrop-blur rounded-full shadow-lg hover:scale-105 transition-transform ${confidenceScore ? 'text-purple-600 ring-2 ring-purple-500' : 'text-gray-700 dark:text-gray-200'} disabled:opacity-50 relative flex-shrink-0`}
                        title={t('analyze_confidence_title', 'Analyze Fit Confidence')}
                    >
                        {isAnalyzingConfidence ? <Spinner className="w-4 h-4 sm:w-5 sm:h-5" /> : <BadgeCheckIcon className="w-4 h-4 sm:w-5 sm:h-5" />}
                        {isFeatureLocked('premium', 'wardrobe_3d') && <LockIcon className="w-2.5 h-2.5 sm:w-3 sm:h-3 absolute top-0 right-0 text-gray-400" />}
                    </button>
                    <button
                        onClick={() => handleFeatureClick('premium', 'style_report', onGenerateReport)}
                        disabled={!displayImageUrl || isGeneratingReport}
                        className={`p-2 sm:p-3 bg-white/90 dark:bg-gray-800/90 backdrop-blur rounded-full shadow-lg hover:scale-105 transition-transform ${styleReport ? 'text-indigo-600 ring-2 ring-indigo-500' : 'text-gray-700 dark:text-gray-200'} disabled:opacity-50 relative flex-shrink-0`}
                        title={t('style_report_title', 'Multi-Agent Style Report')}
                    >
                        {isGeneratingReport ? <Spinner className="w-4 h-4 sm:w-5 sm:h-5" /> : <MessageSquareIcon className="w-4 h-4 sm:w-5 sm:h-5" />}
                        {isFeatureLocked('premium', 'style_report') && <LockIcon className="w-2.5 h-2.5 sm:w-3 sm:h-3 absolute top-0 right-0 text-gray-400" />}
                    </button>
                    <button
                        onClick={() => handleFeatureClick('advanced', 'runway_video', async () => {
                            if (!displayImageUrl) return;
                            setIsVideoLoading(true);
                            try {
                                const base64 = displayImageUrl.split(',')[1] || displayImageUrl;
                                const { operation } = await generateShoppingLookVideo(base64, 'image/jpeg', t('runway_video_prompt'));

                                let currentOp = operation;
                                let isDone = false;
                                const startTime = Date.now();
                                const MAX_POLLING_TIME = 300000;

                                while (!isDone) {
                                    if (Date.now() - startTime > MAX_POLLING_TIME) {
                                        throw new Error(t('error_timeout'));
                                    }
                                    await new Promise(r => setTimeout(r, 5000));
                                    const updated = await getVideosOperationStatus(currentOp);
                                    currentOp = updated.operation;
                                    if (currentOp.done || currentOp.response || currentOp.error) {
                                        isDone = true;
                                    }
                                    if ((currentOp as any).metadata?.state === 'SUCCEEDED' || (currentOp as any).metadata?.state === 'FAILED') {
                                        isDone = true;
                                    }
                                }

                                if (currentOp.error) {
                                    throw new Error((currentOp.error as any).message || 'Unknown operation error');
                                }

                                const { videoUrl: directUrl, downloadUri } = extractVideoUrlFromOperation(currentOp);
                                if (directUrl) {
                                    setVideoUrl(directUrl);
                                } else if (downloadUri) {
                                    const finalVideoUrl = await downloadVideo(downloadUri, apiKeys.gemini);
                                    setVideoUrl(finalVideoUrl);
                                } else {
                                    throw new Error(t('lookbookCreatorModal_error_noVideoLink'));
                                }
                            } catch (e) {
                                console.error('Video generation failed', e);
                            } finally {
                                setIsVideoLoading(false);
                            }
                        })}
                        disabled={!displayImageUrl || isVideoLoading}
                        className={`p-2 sm:p-3 bg-white/90 dark:bg-gray-800/90 backdrop-blur rounded-full shadow-lg hover:scale-105 transition-transform ${videoUrl ? 'text-pink-600 ring-2 ring-pink-500' : 'text-gray-700 dark:text-gray-200'} disabled:opacity-50 relative flex-shrink-0`}
                        title={t('generate_runway_video', 'Generate Runway Video')}
                    >
                        {isVideoLoading ? <Spinner className="w-4 h-4 sm:w-5 sm:h-5" /> : <VideoIcon className="w-4 h-4 sm:w-5 sm:h-5" />}
                        {isFeatureLocked('advanced', 'runway_video') && <LockIcon className="w-2.5 h-2.5 sm:w-3 sm:h-3 absolute top-0 right-0 text-gray-400" />}
                    </button>
                    <button
                        onClick={() => setIsCreativeToolsOpen(!isCreativeToolsOpen)}
                        className={`p-2 sm:p-3 bg-white/90 dark:bg-gray-800/90 backdrop-blur rounded-full shadow-lg hover:scale-105 transition-transform ${isCreativeToolsOpen ? 'text-amber-600 ring-2 ring-amber-500' : 'text-gray-700 dark:text-gray-200'} relative flex-shrink-0`}
                        title={t('creative_tools_title', 'AI Creative Studio')}
                    >
                        <WandSparklesIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                        {isFeatureLocked('premium') && <LockIcon className="w-2.5 h-2.5 sm:w-3 sm:h-3 absolute top-0 right-0 text-gray-400" />}
                    </button>
                    <button
                        onClick={() => handleFeatureClick('advanced', 'wardrobe_3d', handleToggle3D)}
                        className={`p-2 sm:p-3 backdrop-blur rounded-full shadow-lg hover:scale-105 transition-all relative flex-shrink-0 ${is3DMode ? 'bg-indigo-600 text-white' : 'bg-white/90 dark:bg-gray-800/90 text-gray-700 dark:text-gray-200'}`}
                        title={t('canvas_view3D')}
                    >
                        <Rotate3dIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                        {isFeatureLocked('advanced', 'wardrobe_3d') && <LockIcon className="w-2.5 h-2.5 sm:w-3 sm:h-3 absolute top-0 right-0 text-gray-400" />}
                    </button>
                    <div className="flex-shrink-0">
                        <button
                            onClick={() => onNavigate('booking')}
                            className="p-2 sm:p-3 bg-[#C0A172] text-white backdrop-blur rounded-full shadow-lg hover:scale-105 transition-transform flex items-center gap-2 group/book"
                            title={t('portfolioPage_bookService')}
                        >
                            <WandSparklesIcon className="w-4 h-4 sm:w-5 sm:h-5 group-hover/book:rotate-12 transition-transform" />
                            <span className="hidden sm:inline-block max-w-0 overflow-hidden group-hover/book:max-w-xs transition-all duration-300 text-xs font-bold whitespace-nowrap">
                                {t('portfolioPage_bookService')}
                            </span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Scene Controls */}
            <SceneControlPanel
                isOpen={isSceneControlOpen}
                onClose={() => setIsSceneControlOpen(false)}
                config={currentOutfit?.sceneConfig}
                onUpdate={onUpdateSceneConfig}
            />

            {/* Creative Tools Panel */}
            <CreativeToolsPanel
                isOpen={isCreativeToolsOpen}
                onClose={() => setIsCreativeToolsOpen(false)}
                onApplyTool={(type) => handleFeatureClick('premium', 'creative_tools', () => props.onApplyCreativeTool?.(type))}
                isLoading={isLoading}
            />

            {/* Confidence Score Display */}
            <AnimatePresence>
                {confidenceScore && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 20 }}
                        className="absolute bottom-24 right-6 z-50 bg-white dark:bg-gray-800 p-4 rounded-xl shadow-xl border border-indigo-100 dark:border-indigo-900 w-72"
                    >
                        <div className="flex justify-between items-start mb-2">
                            <h4 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
                                <BadgeCheckIcon className="w-5 h-5 text-indigo-500" />
                                {t('confidence_score_title', 'Fit Confidence')}
                            </h4>
                            <button onClick={() => setConfidenceScore(null)} className="text-gray-400 hover:text-gray-600" aria-label={t('common_close')}>
                                <XIcon className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="flex items-center gap-3 mb-3">
                            <div className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 to-purple-500">
                                {confidenceScore.score}%
                            </div>
                            <div className="h-2 flex-grow bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                                <ConfidenceScoreBar score={confidenceScore.score} />
                            </div>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-300 italic">
                            "{confidenceScore.reason}"
                        </p>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Main Content Area (Scrollable) */}
            <div className={`flex-grow overflow-y-auto overflow-x-hidden custom-scrollbar relative font-sans`}>
                <div className="flex flex-col items-center p-4 pb-32 sm:pb-48 min-h-full">
                    <AnimatePresence mode="wait">
                        {is3DMode && view360Images.length > 0 ? (
                            <motion.div
                                key="3d"
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.9 }}
                                className="w-full max-w-2xl aspect-[3/4] relative z-10"
                            >
                                <ThreeSixtyViewer images={view360Images} />
                                {isLoading && (
                                    <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-white/40 dark:bg-gray-800/40 backdrop-blur-[2px]">
                                        <Spinner className="w-12 h-12 text-indigo-600 mb-4" />
                                        {loadingMessage && <p className="text-lg font-semibold text-gray-800 dark:text-white bg-white/80 dark:bg-gray-800/80 px-4 py-2 rounded-full shadow-lg">{loadingMessage}</p>}
                                    </div>
                                )}
                            </motion.div>
                        ) : (comparisonOutfits && comparisonOutfits.length === 2) ? (
                            <motion.div
                                key="compare"
                                className="flex flex-col sm:flex-row gap-4 w-full justify-center items-center relative z-10"
                            >
                                {comparisonOutfits.map((outfit) => (
                                    <div key={outfit.id} className="relative w-full max-w-md aspect-[3/4] bg-white rounded-lg shadow-lg overflow-hidden">
                                        <img src={outfit.previewImageUrl || ''} alt={outfit.name} className="w-full h-full object-cover" />
                                        <div className="absolute bottom-0 left-0 right-0 bg-black/60 p-2 text-white text-center">
                                            <p className="font-bold">{outfit.name}</p>
                                        </div>
                                    </div>
                                ))}
                                {isLoading && (
                                    <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-white/40 dark:bg-gray-800/40 backdrop-blur-[2px]">
                                        <Spinner className="w-12 h-12 text-indigo-600 mb-4" />
                                        {loadingMessage && <p className="text-lg font-semibold text-gray-800 dark:text-white bg-white/80 dark:bg-gray-800/80 px-4 py-2 rounded-full shadow-lg">{loadingMessage}</p>}
                                    </div>
                                )}
                            </motion.div>
                        ) : (
                            <motion.div
                                key="image"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className="relative w-full max-w-2xl aspect-[3/4] shadow-2xl rounded-xl overflow-hidden bg-white dark:bg-gray-800 z-10"
                            >
                                {displayImageUrl ? (
                                    <>
                                        <motion.img
                                            key={displayImageUrl}
                                            src={displayImageUrl}
                                            alt={t('canvas_result_alt')}
                                            className="w-full h-full object-cover"
                                            onLoad={() => setIsImageLoading(false)}
                                            onError={() => setIsImageLoading(false)}
                                            layoutId="generated-model-image"
                                        />
                                        {/* Fit Heatmap Overlay */}
                                        {fitMap && (
                                            <div className="absolute inset-0 pointer-events-none">
                                                {fitMap.map((area, idx) => (
                                                    <FitHeatmapArea key={`${area.name}-${idx}`} area={area} t={t} />
                                                ))}
                                            </div>
                                        )}
                                        {/* Video Overlay */}
                                        {videoUrl && (
                                            <div className="absolute inset-0 z-40 bg-black">
                                                <video
                                                    src={videoUrl}
                                                    autoPlay
                                                    loop
                                                    controls
                                                    className="w-full h-full object-cover"
                                                />
                                                <button
                                                    onClick={() => setVideoUrl(null)}
                                                    className="absolute top-4 left-4 p-2 bg-white/20 hover:bg-white/40 backdrop-blur rounded-full text-white"
                                                    title={t('common_close', 'Close')}
                                                >
                                                    <XIcon className="w-4 h-4" />
                                                </button>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
                                        <CubeIcon className="w-16 h-16 mb-4 opacity-50" />
                                        <p className="text-lg font-medium">{t('canvas_uploadToStart')}</p>
                                    </div>
                                )}

                                {/* Loading Overlay */}
                                <AnimatePresence>
                                    {(isLoading || isImageLoading) && (
                                        <motion.div
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            exit={{ opacity: 0 }}
                                            className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-white/40 dark:bg-gray-800/40 backdrop-blur-[2px]"
                                        >
                                            <Spinner className="w-12 h-12 text-indigo-600 mb-4" />
                                            {(loadingMessage || isImageLoading) && (
                                                <div className="bg-white/80 dark:bg-gray-800/80 px-6 py-3 rounded-full shadow-lg border border-white/50 dark:border-gray-700/50">
                                                    <p className="text-base font-semibold text-gray-800 dark:text-white text-center">
                                                        {loadingMessage || (isImageLoading ? t('loading_rendering') : '')}
                                                    </p>
                                                    {loadingAction && <p className="text-xs text-gray-500 mt-1 text-center">{loadingAction}</p>}
                                                </div>
                                            )}
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                {/* Floating Action Buttons */}
                                {displayImageUrl && !isLoading && (
                                    <div className="absolute top-4 right-4 flex flex-col gap-2 z-30">
                                        {isFavoriteEnabled && (
                                            <button
                                                onClick={onToggleFavorite}
                                                className="p-3 bg-white/90 dark:bg-gray-800/90 backdrop-blur rounded-full shadow-lg hover:scale-105 transition-transform text-gray-700 dark:text-gray-200"
                                                title={isFavorited ? t('canvas_favorite_remove') : t('canvas_favorite_add')}
                                            >
                                                <HeartIcon filled={isFavorited} className={`w-5 h-5 ${isFavorited ? 'text-red-500' : ''}`} />
                                            </button>
                                        )}
                                        {isShopTheLookEnabled && (
                                            <button
                                                onClick={() => onShopTheLook()}
                                                className="p-3 bg-white/90 dark:bg-gray-800/90 backdrop-blur rounded-full shadow-lg hover:scale-105 transition-transform text-gray-700 dark:text-gray-200"
                                                title={t('canvas_shopTheLook')}
                                            >
                                                <ShoppingBagIcon className="w-5 h-5" />
                                            </button>
                                        )}
                                    </div>
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* PoseBar (Moved inside scrollable area before footer) */}
                    <PoseBar {...props} />

                    {/* Footer Section */}
                    <div className="w-full mt-12">
                        <Footer onNavigate={onNavigate} />
                    </div>

                    {/* Error Toast */}
                    <AnimatePresence>
                        {generationError && (
                            <motion.div
                                initial={{ opacity: 0, y: 50 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: 50 }}
                                className="fixed bottom-36 left-1/2 -translate-x-1/2 bg-red-100 dark:bg-red-900/90 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-200 px-6 py-4 rounded-xl shadow-2xl flex items-center gap-4 z-[60] max-w-md"
                            >
                                <span className="text-sm font-medium">{generationError}</span>
                                <button onClick={onClearError} className="p-1 hover:bg-red-200 dark:hover:bg-red-800 rounded-full" title={t('common_close')}>
                                    <XIcon className="w-4 h-4" />
                                </button>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <StyleReportModal
                        isOpen={!!styleReport}
                        report={styleReport}
                        onClose={onCloseReport}
                        onSave={(report) => {
                            if (displayImageUrl && props.onSaveReport) {
                                props.onSaveReport(report, displayImageUrl);
                            }
                        }}
                    />

                    <PremiumFeatureModal
                        isOpen={!!lockedFeature}
                        onClose={() => setLockedFeature(null)}
                        requiredLevel={lockedFeature?.level === 'premium' ? t('profilePage_membership_premium') : t('profilePage_membership_advanced')}
                    />
                </div>
            </div>
        </div>
    );
};

export default Canvas;
